/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import PropTypes from 'prop-types';
import { FiInfo } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {
  Dropdown,
  Modal,
  Button,
  Form,
  Col,
  Row,
  Card,
  OverlayTrigger,
  Tooltip,
  Popover
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TableIconBadge from './TableIconBadge';
import {
  faDownload,
  faFilter,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { BiRightArrow, BiDownArrow } from 'react-icons/bi';
import AppContext from 'context/Context';
import { useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import { getExportAllData } from 'api/endpoints/SubProjectPedologicalServices';
import { get, isEmpty } from 'lodash';
import Model from '../monitoring/Model';
import { getTenantFromToken } from 'utils/routeUtils';

const ProjectNameCell = ({ handleDownload, handleDownloadAll }) => (
  <div className="px-2 my-auto theme-control-toggle">
    <Dropdown
      align="end"
      className="d-inline-block"
      style={{
        backgroundcolor: 'red'
      }}
    >
      <Dropdown.Toggle
        variant="primary"
        id="dropdown-custom-components"
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 500,
          backgroundColor: '#BBCEBF',
          border: 'none',
          overflow: 'hidden'
        }}
        className="p-0"
      >
        <FontAwesomeIcon icon={faDownload} className="fs-5" />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item onClick={handleDownload}>Export</Dropdown.Item>
        <Dropdown.Item onClick={handleDownloadAll}>Export All</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

const ForwardRefFontAwesomeIcon = React.forwardRef((props, ref) => (
  <FontAwesomeIcon {...props} forwardedRef={ref} className="my-2" />
));

const ActionCell = ({
  row,
  onViewClick,
  onUpdateClick,
  onDeleteClick,
  onStartProcessClick
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStartProcessModal, setShowStartProcessModal] = useState(false);

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    onDeleteClick(row);
    handleCloseDeleteModal();
  };

  const handleCloseStartProcessingModal = () => {
    setShowStartProcessModal(false);
  };

  const handleConfirmStartProcessing = () => {
    onStartProcessClick(row);
    handleCloseStartProcessingModal();
  };

  return (
    <>
      <Dropdown drop="left">
        <Dropdown.Toggle
          style={{ cursor: 'pointer' }}
          as={ForwardRefFontAwesomeIcon}
          icon="ellipsis-v"
          pull="right"
        />
      </Dropdown>
    </>
  );
};

ActionCell.propTypes = {
  row: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onUpdateClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onStartProcessClick: PropTypes.func.isRequired
};

const ManageProjectsTable = ({
  id,
  data,
  handleClick,
  type,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
  allMonths,
  hyperlink,
  description
}) => {
  // api call
  const tenant = getTenantFromToken();
  const getExportAllApi = useApi(getExportAllData);

  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);

  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({});
  const [showFilterRow, setShowFilterRow] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const firstObject = data.length > 0 ? data[0] : {};
  // const years = Object.keys(firstObject[type]).filter(key => !isNaN(key));
  const years = Object.keys(firstObject).filter(key => !isNaN(key));

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth(); // 0-based index for months

  const sample = data[0];

  // Handle change of year
  const handleYearChange = useCallback(e => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
  }, []);

  // Handle change of month
  const handleMonthChange = useCallback(e => {
    setSelectedMonth(e.target.value);
  }, []);

  // Function to handle village click
  const handleVillageClick = useCallback(
    (e, village, id) => {
      e.preventDefault();
      const district = firstObject.district;
      const block = firstObject.block;
      handleClick(village, district, block, id);
    },
    [firstObject.district, firstObject.block]
  );

  const getColumns = type => {
    const baseColumns = [
      {
        Header: 'S No:',
        id: 'row',
        Cell: ({ row }) => <div>{row.index + 1}</div>
      },
      { accessor: 'District', Header: 'District' },
      { accessor: 'Block', Header: 'Block' },
      {
        accessor: 'Village',
        Header: 'Village',
        Cell: ({ value, row }) =>
          hyperlink ? (
            <a
              href="#"
              style={{ color: 'blue' }}
              onClick={e => handleVillageClick(e, value, row.original.id)}
            >
              {value}
            </a>
          ) : (
            value
          )
      }
    ];

    const dataColumns =
      id !== 'SOC'
        ? [
            { accessor: 'Soil_Texture', Header: 'Soil Texture' },
            {
              accessor: 'Soil_Water_Retention',
              Header: 'Soil Water Retention (%)'
            },
            {
              accessor: 'Status',
              Header: 'Status',
              Cell: ({ value, row }) => (
                <span style={{ color: row.original.color || 'black' }}>
                  {value}
                </span>
              )
            }
          ]
        : [
            { accessor: 'Min', Header: 'Soc Min (%)' },
            { accessor: 'Max', Header: 'Soc Max (%)' }
          ];

    return [...baseColumns, ...dataColumns];
  };

  const extractData = (obj, year, month, id) => {
    const yearData = obj[year] || {};
    const monthData = yearData[month] || {};

    const formatNumber = num => {
      return num !== undefined ? Number(num.toFixed(3)) : '-';
    };

    if (id == 'SOIL_WATER_RETENTION_CAPACITY') {
      return {
        Soil_Water_Retention: formatNumber(monthData.Soil_Water_Retention),
        Status: monthData.status || '-',
        Soil_Texture: monthData.soil_texture || '-',
        color: monthData.color
      };
    } else {
      return {
        Min: formatNumber(monthData.min),
        Max: formatNumber(monthData.max)
      };
    }
  };

  const columns = useMemo(() => getColumns(id), [id]);

  const tableData = useMemo(() => {
    return data.map(obj => {
      const extractedData = extractData(obj, selectedYear, selectedMonth, id);
      return {
        District: obj.district,
        Block: obj.block,
        Village: obj.village,
        ...extractedData,
        id: obj.id
      };
    });
  }, [data, selectedYear, selectedMonth, id]);

  const convertToCSV = arr => {
    // Extract headers dynamically from the first object, excluding the 'id' field
    const headers = Object.keys(arr[0]).filter(
      key => key !== 'id' && key !== 'color'
    );

    // Map data rows, excluding 'id' field
    const dataRows = arr.map(obj => {
      return headers.map(header => obj[header]);
    });

    // Combine headers, data rows, and additional row into CSV format
    const csvContent = [
      headers.join(','), // Join headers with commas
      ...dataRows.map(row => row.join(',')) // Join each data row with commas
    ].join('\n'); // Join all rows with newlines

    return csvContent;
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFilterToggle = () => {
    setShowFilterRow(!showFilterRow);
  };

  const handleFilterChange = (columnId, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  useEffect(() => {
    const filtered = tableData.filter(row =>
      Object.entries(filters).every(([columnId, filterValue]) => {
        const cellValue = row[columnId]?.toString().toLowerCase();
        const filterValueLower = filterValue.toLowerCase();
        return cellValue.startsWith(filterValueLower);
      })
    );
    setFilteredData(filtered);
  }, [filters, tableData]);

  const handleDownload = () => {
    const csvData = convertToCSV(tableData);
    const filename = `${type}(${selectedMonth}-${selectedYear}).csv`;
    downloadCSV(csvData, filename);
  };

  const handleDownloadAll = () => {
    // Your download all logic here
    let tenant_id = tenant;
    let parameters = [id];
    let params = {
      tenant_id,
      parameters
    };
    getExportAllApi.request(params);
  };

  // Function to download the file from S3 link
  const downloadFile = url => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  // Update block list when blocks API data changes
  useEffect(() => {
    const blocks = get(getExportAllApi, 'data.results', []);

    if (blocks && blocks.length > 0) {
      const s3Link = blocks[0]; // Assuming the S3 link is in the first block
      downloadFile(s3Link);
    }
  }, [getExportAllApi.data]);

  const handleExportClick = event => {
    setShow(!show);
    setTarget(event.target);
  };

  const Dropdown = (
    <Row className="mb-2">
      <Col lg={{ span: 2, offset: 8 }} className="d-flex justify-content-end">
        {/* Dropdown for selecting year */}
        <Form.Select onChange={handleYearChange} value={selectedYear}>
          {years.map((year, index) => (
            <option key={index} value={year}>
              {year}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col lg={2} className="d-flex justify-content-end">
        {/* Dropdown for selecting month */}
        <Form.Select onChange={handleMonthChange} value={selectedMonth}>
          {allMonths?.map(month => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </Form.Select>
      </Col>
    </Row>
  );

  const popover = (
    <Popover id="popover-basic" style={{ position: 'relative' }}>
      <Popover.Body>
        <div className="d-flex flex-column">
          <Button
            variant="link"
            onClick={handleDownload}
            style={{ padding: '5px' }}
          >
            Export
          </Button>
          <Button
            variant="link"
            onClick={handleDownloadAll}
            style={{ padding: '5px' }}
          >
            Export All
          </Button>
        </div>
      </Popover.Body>
      <style>
        {`
          .popover {
            position: relative;
          }
          .popover .popover-arrow {
            display: none !important;
          }
        `}
      </style>
    </Popover>
  );

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  return (
    <Card className="mt-2">
      <FalconCardHeader
        title={type}
        titleTag="h5"
        // className="p-0 my-auto"
        light={true}
        infoBtn={!isEmpty(description) && <FiInfo onClick={handleShowModal} />}
        endEl={
          <div className="d-flex justify-content-end my-auto">
            <div
              className="px-2 my-auto theme-control-toggle"
              onClick={handleFilterToggle}
            >
              <OverlayTrigger
                key="filter"
                placement={'bottom'}
                overlay={<Tooltip id="FilterData">Filter</Tooltip>}
              >
                <div className="theme-control-toggle-label">
                  <FontAwesomeIcon icon={faFilter} className="fs-0" />
                </div>
              </OverlayTrigger>
            </div>
            <div
              className="px-2 my-auto theme-control-toggle"
              onClick={handleExportClick}
            >
              <OverlayTrigger
                trigger="click"
                placement="left"
                show={show}
                target={target}
                onClick={() => setShow(!show)}
                overlay={popover}
              >
                <div className="theme-control-toggle-label">
                  <FontAwesomeIcon icon={faDownload} className="fs-0" />
                </div>
              </OverlayTrigger>
            </div>
          </div>
        }
      />
      <Card.Body>
        {Dropdown}

        <AdvanceTableWrapper
          columns={columns}
          data={filteredData || tableData}
          pagination
          sortable
          perPage={10}
          className="min-vh-50 py-5"
        >
          <AdvanceTable
            table
            headerClassName="bg-200 text-900 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: 'fs--1 mb-0'
            }}
            filters={filters}
            handleFilterChange={handleFilterChange}
            showFilterRow={showFilterRow}
          />
          <div className="mt-3">
            <AdvanceTableFooter
              rowCount={data.length}
              table
              rowInfo
              navButtons
              rowsPerPageSelection
              pageSize={10}
            />
          </div>
        </AdvanceTableWrapper>
      </Card.Body>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={description}
        title={type}
      />
    </Card>
  );
};

ManageProjectsTable.propTypes = {
  data: PropTypes.array.isRequired,
  allMonths: PropTypes.arrayOf(PropTypes.string).isRequired
};

AdvanceTableWrapper.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      Cell: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          value: PropTypes.func.isRequired,
          row: PropTypes.shape({
            original: PropTypes.object.isRequired
          }).isRequired
        })
      ])
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.bool,
  perPage: PropTypes.number
};

export default ManageProjectsTable;
